import React, { useState, useMemo, useEffect, useCallback } from "react";
import { Trans, useTranslation } from "react-i18next";
import Icon from "../icons";
import Loader from "../loader";

// Pure helpers — module scope so they don't get a new identity on every render
// (which would break the useCallback'd handlers that depend on them).
function collectDescendants<T extends TableRow>(itemId: T["id"], data: T[] | null): T[] {
    if (!data) return [];
    const descendants: T[] = [];
    const directChildren = data.filter((item) => item.parentId === itemId);
    for (const child of directChildren) {
        descendants.push(child);
        descendants.push(...collectDescendants<T>(child.id, data));
    }
    return descendants;
}

export interface TableColumn<T extends TableRow> {
    key: keyof T | string;
    label: string | React.ReactNode;
    sortable?: boolean;
    render?: (
        item: T,
        context?: {
            expandedRowId?: T["id"] | null;
            toggleRowDetails?: (id: T["id"]) => void;
            /** True while this row is expanded but its details panel isn't mounted yet. */
            isDetailsLoading?: boolean;
        }
    ) => React.ReactNode;
    align?: "left" | "center" | "right";
    width?: string | number;
    className?: string;
    cellClassName?: string;
}

export interface TableRow {
    id: number | string;
    level?: number;
    parentId?: number | string;
    expanded?: boolean;
    hasChildren?: boolean;
    avatar?: React.ReactNode;
    [key: string]: any;
}

interface InfiniteScrollConfig {
    onLoadMore: (page: number, pageSize: number) => void;
    hasMore: boolean;
    isLoading?: boolean;
    initialPage?: number;
    pageSize?: number;
}

interface ReusableTableProps<T extends TableRow> {
    data: T[] | null;
    columns: TableColumn<T>[];
    enableSorting?: boolean;
    enableDrag?: boolean;
    enableExpand?: boolean;
    getRowActions?: (item: T) => React.ReactNode;
    onDataChange?: (newData: T[]) => void;
    indentSize?: number;
    isRearranging?: boolean;
    pagination?: any;
    renderRowDetails?: (item: T) => React.ReactNode;
    /**
     * Opt in to a brief loading state before an expanded row's details mount.
     * Only worth enabling when the details panel is expensive enough that the
     * click would otherwise appear to do nothing — for cheap panels this just
     * adds latency, so it defaults to off and details render immediately.
     */
    showRowDetailsLoading?: boolean;
    /** Message shown while the details panel is being prepared. */
    rowDetailsLoadingLabel?: string;
    paddingBottomNeeded?: boolean;
    topClassName?: string;
    overflowVisible?: boolean;
    fixedHeight?: string | number;
    enableStickyHeader?: boolean;
    infiniteScrollConfig?: InfiniteScrollConfig;
    noWrapHeaders?: boolean;
}

/**
 * How long the expanded-row loading state stays up before the details mount.
 *
 * A deliberate floor, not padding. Deferring by animation frames alone (~32ms)
 * technically shows a spinner but far too briefly to perceive, so the click still
 * read as "nothing happened". ~300ms registers as feedback and doubles as the
 * yield the browser needs to paint before an expensive details panel (e.g. a
 * nested table of variant editors) blocks the main thread.
 */
const ROW_DETAILS_LOADING_MS = 300;

interface DetailsRowProps {
    colSpan: number;
    isDetailsLoading: boolean;
    loadingLabel: string;
    renderDetails: () => React.ReactNode;
}

/**
 * The expanded row's details panel, memoized separately from the row's cells.
 *
 * Without this split, swapping the loading state for the real content re-rendered
 * the whole row — for the bulk-update table that means 17 cells including nine
 * react-selects — so both the loader and the content appeared later than the click
 * that asked for them.
 */
const DetailsRow = React.memo(function DetailsRow({
    colSpan,
    isDetailsLoading,
    loadingLabel,
    renderDetails,
}: DetailsRowProps) {
    return (
        <tr>
            <td colSpan={colSpan} className="px-0 py-0 border-b border-[#F0F0F0]">
                <div className="-mx-[5px] bg-white shadow-md border border-gray-200 p-6">
                    {isDetailsLoading ? (
                        <div className="flex items-center gap-2 text-sm font-medium text-gray-600">
                            <InlineSpinner />
                            {loadingLabel}
                        </div>
                    ) : (
                        renderDetails()
                    )}
                </div>
            </td>
        </tr>
    );
});

/** Inline spinner, sized to sit inside a row's expand/collapse control. */
export const InlineSpinner: React.FC<{ className?: string }> = ({
    className = "w-4 h-4",
}) => (
    <svg className={`${className} animate-spin text-secondary`} viewBox="0 0 24 24" fill="none">
        <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="3" className="opacity-20" />
        <path d="M22 12a10 10 0 0 0-10-10" stroke="currentColor" strokeWidth="3" strokeLinecap="round" />
    </svg>
);

interface TableBodyRowProps<T extends TableRow> {
    item: T;
    columns: TableColumn<T>[];
    enableExpand: boolean;
    indentSize: number;
    isExpanded: boolean;
    isDragging: boolean;
    isDragOver: boolean;
    canDrop: boolean;
    dropIndicatorClass: string;
    draggableRow: boolean;
    getRowActions?: (item: T) => React.ReactNode;
    renderRowDetails?: (item: T) => React.ReactNode;
    rowDetailsLoadingLabel?: string;
    isDetailsLoading: boolean;
    toggleExpanded: (id: T["id"]) => void;
    toggleRowDetails: (id: T["id"]) => void;
    onDragStart: (e: React.DragEvent, item: T) => void;
    onDragOver: (e: React.DragEvent, item: T) => void;
    onDragLeave: (e: React.DragEvent) => void;
    onDrop: (e: React.DragEvent, item: T) => void;
    onDragEnd: () => void;
}

/**
 * One table row, memoized.
 *
 * Expand/collapse state lives inside ReusableTable, so toggling it used to
 * re-render every row in the table — with 1500+ rows of form controls that meant
 * a multi-second freeze before the details panel appeared. Because the props that
 * come from the parent keep their identity during an internal state change, memo
 * lets only the two affected rows re-render.
 *
 * Note: `expandedRowId` is passed to column renderers as this row's own id (or
 * null). Renderers only ever compare it against their own row id, so behaviour is
 * unchanged, but the value no longer differs per row on every expand.
 */
const TableBodyRow = React.memo(function TableBodyRow<T extends TableRow>({
    item,
    columns,
    enableExpand,
    indentSize,
    isExpanded,
    isDragging,
    isDragOver,
    canDrop,
    dropIndicatorClass,
    draggableRow,
    getRowActions,
    renderRowDetails,
    rowDetailsLoadingLabel,
    isDetailsLoading,
    toggleExpanded,
    toggleRowDetails,
    onDragStart,
    onDragOver,
    onDragLeave,
    onDrop,
    onDragEnd,
}: TableBodyRowProps<T>) {
    const { t } = useTranslation("common");
    const renderContext = {
        expandedRowId: isExpanded ? item.id : null,
        toggleRowDetails,
        isDetailsLoading,
    };

    const renderDetails = useCallback(
        () => renderRowDetails?.(item),
        [renderRowDetails, item]
    );

    return (
        <>
            <tr
                draggable={draggableRow}
                onDragStart={(e) => onDragStart(e, item)}
                onDragOver={(e) => onDragOver(e, item)}
                onDragLeave={onDragLeave}
                onDrop={(e) => onDrop(e, item)}
                onDragEnd={onDragEnd}
                className={`
                                        border-b border-[#F0F0F0]
                                        ${isDragging ? "opacity-50 bg-gray-100" : ""}
                                        ${isDragOver && canDrop ? "bg-blue-50 " + dropIndicatorClass : ""}
                                        ${draggableRow ? "hover:bg-gray-50" : ""}
                                        ${isExpanded ? "" : ""}
                                        transition-colors duration-150
                                    `}
            >
                {columns.map((col, colIndex) => (
                    <td key={String(colIndex)} className={`px-3 py-3 text-sm${col.cellClassName ? " " + col.cellClassName : ""}`}>
                        {colIndex === 0 && enableExpand ? (
                            <div
                                className="flex items-center"
                                style={{ paddingLeft: `${(item.level || 0) * indentSize}px` }}
                            >
                                {draggableRow && (
                                    <div className="mr-2 cursor-grab active:cursor-grabbing">
                                        <Icon
                                            name="grip-vertical"
                                            className="w-3 h-3 text-gray-400"
                                        />
                                    </div>
                                )}
                                {item.hasChildren ? (
                                    <button
                                        type="button"
                                        onClick={() => toggleExpanded(item.id)}
                                        className="mr-2 p-0.5 hover:bg-gray-200 rounded"
                                    >
                                        <Icon
                                            name={item.expanded ? "arrowdown" : "arrowright"}
                                            className="w-3 h-3 text-gray-500"
                                        />
                                    </button>
                                ) : enableExpand ? (
                                    <div className="w-6 mr-2" />
                                ) : null}
                                {item.avatar && (
                                    <div className="w-6 h-6 bg-gray-100 rounded-full flex items-center justify-center text-xs mr-3">
                                        {item.avatar}
                                    </div>
                                )}
                                {col.render
                                    ? col.render(item, renderContext)
                                    : item[col.key]}
                            </div>
                        ) : col.render ? (
                            col.render(item, renderContext)
                        ) : (
                            item[col.key]
                        )}
                    </td>
                ))}
                {getRowActions && (
                    <td className="px-3 py-3 text-sm">
                        <div className="flex justify-center space-x-2">
                            {getRowActions(item)}
                        </div>
                    </td>
                )}
            </tr>
            {/* Details Panel Row */}
            {isExpanded && renderRowDetails && (
                <DetailsRow
                    colSpan={getRowActions ? columns.length + 1 : columns.length}
                    isDetailsLoading={isDetailsLoading}
                    loadingLabel={rowDetailsLoadingLabel ?? t("table.loading")}
                    renderDetails={renderDetails}
                />
            )}
        </>
    );
}) as <T extends TableRow>(props: TableBodyRowProps<T>) => JSX.Element;

function ReusableTable<T extends TableRow>({
    data,
    columns,
    enableSorting = true,
    enableDrag = false,
    enableExpand = true,
    getRowActions,
    onDataChange,
    indentSize = 20,
    isRearranging = false,
    pagination = null,
    renderRowDetails,
    showRowDetailsLoading = false,
    rowDetailsLoadingLabel,
    paddingBottomNeeded = false,
    topClassName = "",
    overflowVisible = false,
    fixedHeight,
    enableStickyHeader = true,
    infiniteScrollConfig,
    noWrapHeaders = false,
}: ReusableTableProps<T>) {
    const { t } = useTranslation("common");
    const [tableData, setTableData] = useState<T[] | null>(data);
    const [sortConfig, setSortConfig] = useState<{ key: string | null; direction: "asc" | "desc" | "none" }>({
        key: null,
        direction: "none",
    });
    const [draggedItem, setDraggedItem] = useState<T | null>(null);
    const [dragOverItem, setDragOverItem] = useState<T | null>(null);
    const [expandedRowId, setExpandedRowId] = useState<T["id"] | null>(null);
    // The row whose details panel has finished its loading floor and may mount.
    const [detailsReadyId, setDetailsReadyId] = useState<T["id"] | null>(null);
    const [currentPage, setCurrentPage] = useState(infiniteScrollConfig?.initialPage || 1);
    const [rowsPerPage, setRowsPerPage] = useState(infiniteScrollConfig?.pageSize || 10);
    
    // Drag-to-scroll state (for horizontal scroll)
    const [isDraggingScroll, setIsDraggingScroll] = useState(false);
    const [scrollStartX, setScrollStartX] = useState(0);
    const [scrollLeft, setScrollLeft] = useState(0);
    const [isScrollHelpDismissed, setIsScrollHelpDismissed] = useState(false);
    
    // Separate refs for different scroll behaviors
    const scrollContainerRef = React.useRef<HTMLDivElement>(null); // For horizontal drag-to-scroll
    const infiniteScrollContainerRef = React.useRef<HTMLDivElement>(null); // For infinite scroll
    // Determine if drag-to-scroll should be on the inner scroll container(this is implemented for handling fixed vertical scroll with horizontal scroll)
const useInnerScrollForDrag = overflowVisible && fixedHeight && !infiniteScrollConfig;

    // Update tableData when data prop changes
    useEffect(() => {
        setTableData(data);
    }, [data]);

    // Infinite scroll logic
    useEffect(() => {
        if (!infiniteScrollConfig) return;
        
        const container = infiniteScrollContainerRef.current;
        if (!container) return;

        let lastRequestedPage = currentPage;

        const handleScroll = () => {
            const { scrollTop, scrollHeight, clientHeight } = container;
            
            // Check if near bottom (within 100px)
            if (
                scrollHeight - scrollTop - clientHeight < 100 &&
                infiniteScrollConfig.hasMore &&
                !infiniteScrollConfig.isLoading
            ) {
                const nextPage = currentPage + 1;
                if (lastRequestedPage === currentPage) {
                    lastRequestedPage = nextPage;
                    setCurrentPage(nextPage);
                    infiniteScrollConfig.onLoadMore(nextPage, infiniteScrollConfig.pageSize || 10);
                }
            }
        };

        container.addEventListener('scroll', handleScroll);
        return () => container.removeEventListener('scroll', handleScroll);
    }, [infiniteScrollConfig, currentPage]);

    const toggleExpanded = useCallback((id: T["id"]) => {
        setTableData((prev) => {
            if (!prev) return prev;
            const updated = prev.map((item) => (item.id === id ? { ...item, expanded: !item.expanded } : item));
            onDataChange?.(updated);
            return updated;
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [onDataChange]);

    const toggleRowDetails = useCallback((id: T["id"]) => {
        // Reset readiness first so expanding always shows the loading state, even
        // when moving straight from one expanded row to another.
        setDetailsReadyId(null);
        setExpandedRowId((prev) => (prev === id ? null : id));
    }, []);

    // Hold the loading state for ROW_DETAILS_LOADING_MS after a row is expanded.
    // This both guarantees the indicator is perceivable and gives the browser a
    // chance to paint it before an expensive details panel blocks the main thread.
    useEffect(() => {
        if (!showRowDetailsLoading || expandedRowId === null) return;
        const timer = setTimeout(() => setDetailsReadyId(expandedRowId), ROW_DETAILS_LOADING_MS);
        return () => clearTimeout(timer);
    }, [showRowDetailsLoading, expandedRowId]);

    const isItemVisible = (item: T) => {
        if (!enableExpand || item.level === 0 || !tableData) return true;

        let currentParentId = item.parentId;
        while (currentParentId) {
            const parent = tableData.find((d) => d.id === currentParentId);
            if (!parent || !parent.expanded) return false;
            currentParentId = parent.parentId;
        }
        return true;
    };

    // Helper function to get all descendants of an item
    const getAllDescendants = collectDescendants<T>;

    // Helper function to get the complete subtree (item + all descendants)
    const getSubtree = (item: T, data: T[] | null): T[] => {
        if (!data) return [item];
        return [item, ...getAllDescendants(item.id, data)];
    };

    const visibleData = useMemo(() => {
        if (tableData == null) return null;
        return tableData.filter(isItemVisible);
    }, [tableData]);

    const handleSort = (key: string) => {
        if (!enableSorting || !tableData) return;
        
        let direction: "asc" | "desc" | "none";
        if (sortConfig.key !== key) {
            // First click on a new column - start with ascending
            direction = "asc";
        } else {
            // Cycle through: asc → desc → none → asc
            if (sortConfig.direction === "asc") {
                direction = "desc";
            } else if (sortConfig.direction === "desc") {
                direction = "none";
            } else {
                direction = "asc";
            }
        }
        
        setSortConfig({ key, direction });

        // If direction is 'none', reset to original data order
        if (direction === "none") {
            if (data) {
                setTableData([...data]);
                onDataChange?.(data);
            }
            return;
        }

        const sorted = [...tableData];

        // Helper function to get sort value from item
        const getSortValue = (item: T, key: string) => {
            // Handle special custom keys for sorting
            if (key === "startDate") {
                return item.dateRanges && Array.isArray(item.dateRanges) && item.dateRanges.length > 0
                    ? item.dateRanges[0]?.startDate
                    : null;
            }
            if (key === "endDate") {
                return item.dateRanges && Array.isArray(item.dateRanges) && item.dateRanges.length > 0
                    ? item.dateRanges[0]?.endDate
                    : null;
            }

            // Handle nested properties like "dateRanges[0].startDate"
            if (key.includes("[") || key.includes(".")) {
                const keys = key.replace(/\[(\d+)\]/g, ".$1").split(".");
                let value = item;
                for (const k of keys) {
                    if (value && typeof value === "object") {
                        value = (value as any)[k];
                    } else {
                        return null;
                    }
                }
                return value;
            }

            // Handle direct property access or array properties
            const value = item[key];

            // Special handling for common data structures
            if (Array.isArray(value)) {
                if (key === "dateRanges" && value.length > 0) {
                    // For date ranges, sort by the first date range's start date
                    return value[0]?.startDate || value[0]?.endDate || null;
                }
                if (key === "weeklySchedules" && value.length > 0) {
                    // For weekly schedules, sort by the first day of week
                    return value[0]?.dayOfWeek || null;
                }
                if (key === "locations" && value.length > 0) {
                    // For locations, sort by the first location name
                    return value[0]?.locationName || null;
                }
                // For other arrays, use the length or first item
                return value.length > 0 ? value[0] : null;
            }

            return value;
        };

        // Check if we have hierarchical data (items with meaningful levels and parent relationships)
        const hasHierarchy = sorted.some(item => 
            (item.level && item.level > 0) || item.parentId || item.hasChildren
        );

        if (hasHierarchy) {
            // Group items by level and parent for hierarchical data
            const sortHierarchically = (items: T[], currentLevel: number = 0): T[] => {
                const result: T[] = [];

                // Get items at current level
                const currentLevelItems = items.filter((item) => (item.level || 0) === currentLevel);

                // Sort items at current level
                currentLevelItems.sort((a, b) => {
                    const aValue = getSortValue(a, key);
                    const bValue = getSortValue(b, key);

                    // Handle null/undefined values
                    if (aValue === null || aValue === undefined) {
                        if (bValue === null || bValue === undefined) return 0;
                        return direction === "asc" ? 1 : -1;
                    }
                    if (bValue === null || bValue === undefined) {
                        return direction === "asc" ? -1 : 1;
                    }

                    // Handle numbers
                    if (typeof aValue === "number" && typeof bValue === "number") {
                        return direction === "asc" ? aValue - bValue : bValue - aValue;
                    }

                    // Handle dates
                    if (aValue instanceof Date && bValue instanceof Date) {
                        return direction === "asc"
                            ? aValue.getTime() - bValue.getTime()
                            : bValue.getTime() - aValue.getTime();
                    }

                    // Handle date strings
                    const aDate = new Date(aValue);
                    const bDate = new Date(bValue);
                    if (!isNaN(aDate.getTime()) && !isNaN(bDate.getTime())) {
                        return direction === "asc" ? aDate.getTime() - bDate.getTime() : bDate.getTime() - aDate.getTime();
                    }

                    // Handle strings (default case)
                    return (
                        String(aValue).localeCompare(String(bValue), undefined, { numeric: true }) *
                        (direction === "asc" ? 1 : -1)
                    );
                });

                // For each sorted item at current level, add it and its descendants
                currentLevelItems.forEach((item) => {
                    result.push(item);

                    // Get all descendants and sort them hierarchically
                    const descendants = getAllDescendants(item.id, items);
                    if (descendants.length > 0) {
                        const sortedDescendants = sortHierarchically(descendants, currentLevel + 1);
                        result.push(...sortedDescendants);
                    }
                });

                return result;
            };

            // Get root items (level 0) and sort hierarchically
            const sortedHierarchy = sortHierarchically(sorted, 0);
            setTableData(sortedHierarchy);
            onDataChange?.(sortedHierarchy);
        } else {
            // Simple flat sorting for non-hierarchical data
            sorted.sort((a, b) => {
                const aValue = getSortValue(a, key);
                const bValue = getSortValue(b, key);

                // Handle null/undefined values
                if (aValue === null || aValue === undefined) {
                    if (bValue === null || bValue === undefined) return 0;
                    return direction === "asc" ? 1 : -1;
                }
                if (bValue === null || bValue === undefined) {
                    return direction === "asc" ? -1 : 1;
                }

                // Handle numbers
                if (typeof aValue === "number" && typeof bValue === "number") {
                    return direction === "asc" ? aValue - bValue : bValue - aValue;
                }

                // Handle dates
                if (aValue instanceof Date && bValue instanceof Date) {
                    return direction === "asc"
                        ? aValue.getTime() - bValue.getTime()
                        : bValue.getTime() - aValue.getTime();
                }

                // Handle date strings
                const aDate = new Date(aValue);
                const bDate = new Date(bValue);
                if (!isNaN(aDate.getTime()) && !isNaN(bDate.getTime())) {
                    return direction === "asc" ? aDate.getTime() - bDate.getTime() : bDate.getTime() - aDate.getTime();
                }

                // Handle strings (default case)
                return (
                    String(aValue).localeCompare(String(bValue), undefined, { numeric: true }) *
                    (direction === "asc" ? 1 : -1)
                );
            });

            setTableData(sorted);
            onDataChange?.(sorted);
        }
    };

    const handleDragStart = useCallback((e: React.DragEvent, item: T) => {
        if (!isRearranging || !enableDrag) return;

        setDraggedItem(item);
        e.dataTransfer.effectAllowed = "move";
        e.dataTransfer.setData("text/plain", "");
    }, [isRearranging, enableDrag]);

    const handleDragOver = useCallback((e: React.DragEvent, item: T) => {
        if (!isRearranging || !enableDrag || !draggedItem || !tableData) return;

        // Only allow drop if items are at the same level and have the same parent
        const canDrop =
            draggedItem.level === item.level &&
            draggedItem.parentId === item.parentId &&
            draggedItem.id !== item.id &&
            // Prevent dropping on descendants
            !getAllDescendants(draggedItem.id, tableData).some((desc) => desc.id === item.id);

        if (canDrop) {
            e.preventDefault();
            setDragOverItem(item);
        }
    }, [isRearranging, enableDrag, draggedItem, tableData]);

    const handleDragLeave = useCallback((e: React.DragEvent) => {
        if (!e.currentTarget.contains(e.relatedTarget as Node)) {
            setDragOverItem(null);
        }
    }, []);

    const handleDrop = useCallback((e: React.DragEvent, targetItem: T) => {
        if (!isRearranging || !enableDrag || !draggedItem || draggedItem.id === targetItem.id || !tableData) {
            setDragOverItem(null);
            return;
        }

        e.preventDefault();

        // Only allow drop if items are at the same level and have the same parent
        if (draggedItem.level !== targetItem.level || draggedItem.parentId !== targetItem.parentId) {
            setDragOverItem(null);
            return;
        }

        // Prevent dropping on descendants
        if (getAllDescendants(draggedItem.id, tableData).some((desc) => desc.id === targetItem.id)) {
            setDragOverItem(null);
            return;
        }

        setTableData((prev) => {
            if (!prev) return prev;

            const updated = [...prev];

            // Get the complete subtree (dragged item + all its descendants)
            const draggedSubtree = getSubtree(draggedItem, updated);

            // Find indices
            const draggedIndex = updated.findIndex((item) => item.id === draggedItem.id);
            const targetIndex = updated.findIndex((item) => item.id === targetItem.id);

            if (draggedIndex === -1 || targetIndex === -1) return prev;

            // Remove the entire subtree from current position
            const subtreeIds = new Set(draggedSubtree.map((item) => item.id));
            const remainingItems = updated.filter((item) => !subtreeIds.has(item.id));

            // Find the new target index in the remaining items
            const newTargetIndex = remainingItems.findIndex((item) => item.id === targetItem.id);

            // Insert the entire subtree before the target
            const result = [
                ...remainingItems.slice(0, newTargetIndex),
                ...draggedSubtree,
                ...remainingItems.slice(newTargetIndex),
            ];

            onDataChange?.(result);
            return result;
        });

        setDraggedItem(null);
        setDragOverItem(null);
    }, [isRearranging, enableDrag, draggedItem, tableData, onDataChange]);

    const handleDragEnd = useCallback(() => {
        setDraggedItem(null);
        setDragOverItem(null);
    }, []);

    const getSortIcon = (columnKey: string) => {
        if (sortConfig.key !== columnKey || sortConfig.direction === "none") {
            return <Icon name="arrowupdown" size={16} className="opacity-40" />;
        }
        return sortConfig.direction === "asc" ? (
            <Icon name="arrowup" size={16} className="opacity-70" />
        ) : (
            <Icon name="arrowdown" size={16} className="opacity-70" />
        );
    };

    const canDragItem = useCallback((item?: T) => {
        return Boolean(enableDrag && isRearranging);
    }, [enableDrag, isRearranging]);

    const getDropIndicatorClass = (item: T) => {
        if (!draggedItem || !dragOverItem || dragOverItem.id !== item.id) return "";

        // Show drop indicator only for items at the same level with same parent
        if (draggedItem.level === item.level && draggedItem.parentId === item.parentId) {
            return "border-t-2 border-blue-500";
        }

        return "";
    };

    const paginatedData = useMemo(() => {
        if (!visibleData) return null;
        const startIndex = (currentPage - 1) * rowsPerPage;
        const endIndex = startIndex + rowsPerPage;
        return visibleData.slice(startIndex, endIndex);
    }, [visibleData, currentPage, rowsPerPage]);

    const totalPages = Math.ceil((visibleData?.length ?? 0) / rowsPerPage);

    // Drag-to-scroll handlers
    const handleMouseDown = (e: React.MouseEvent) => {
        if (!overflowVisible || !scrollContainerRef.current) return;
        setIsDraggingScroll(true);
        setScrollStartX(e.pageX - scrollContainerRef.current.offsetLeft);
        setScrollLeft(scrollContainerRef.current.scrollLeft);
        scrollContainerRef.current.style.cursor = 'grabbing';
        scrollContainerRef.current.style.userSelect = 'none';
    };

    const handleMouseLeaveOrUp = () => {
        if (!overflowVisible || !scrollContainerRef.current) return;
        setIsDraggingScroll(false);
        scrollContainerRef.current.style.cursor = 'grab';
        scrollContainerRef.current.style.userSelect = 'auto';
    };

    const handleMouseMove = (e: React.MouseEvent) => {
        if (!isDraggingScroll || !overflowVisible || !scrollContainerRef.current) return;
        e.preventDefault();
        const x = e.pageX - scrollContainerRef.current.offsetLeft;
        const walk = (x - scrollStartX) * 1.5; // Scroll speed multiplier
        scrollContainerRef.current.scrollLeft = scrollLeft - walk;
    };

    return (
        <div>
            {overflowVisible && !isScrollHelpDismissed && (
                <div className="bg-blue-50 border-l-4 border-secondary px-3 py-2 mb-3 rounded-r relative">
                    <button
                        type="button"
                        onClick={() => setIsScrollHelpDismissed(true)}
                        className="absolute top-2 right-2 text-gray-400 hover:text-gray-600 transition-colors"
                        aria-label={t("table.closeHint")}
                    >
                        <Icon name="close" size={14} />
                    </button>
                    <p className="text-xs text-secondary font-medium flex items-center gap-2 pr-6">
                        <Icon name="info" size={14} />
                        <span className="flex-1">
                            <Trans
                                i18nKey="table.horizontalScrollHint"
                                ns="common"
                                components={{
                                    strong: <strong />,
                                    kbd: <kbd className="px-1.5 py-0.5 bg-white border border-blue-300 rounded text-[10px] font-mono" />,
                                }}
                            />
                        </span>
                    </p>
                </div>
            )}
            {/* <div
                ref={overflowVisible ? scrollContainerRef : undefined}
                className={`bg-white border border-[#043E6133] rounded-[5px] p-[5px] ${topClassName ?? ""} ${
                    fixedHeight || infiniteScrollConfig 
                        ? "flex flex-col" 
                        : overflowVisible 
                            ? "overflow-x-auto cursor-grab active:cursor-grabbing" 
                            : paddingBottomNeeded 
                                ? "" 
                                : "overflow-hidden"
                }`}
                style={(fixedHeight || infiniteScrollConfig) ? { maxHeight: typeof fixedHeight === 'number' ? `${fixedHeight}px` : fixedHeight } : undefined}
                onMouseDown={overflowVisible ? handleMouseDown : undefined}
                onMouseLeave={overflowVisible ? handleMouseLeaveOrUp : undefined}
                onMouseUp={overflowVisible ? handleMouseLeaveOrUp : undefined}
                onMouseMove={overflowVisible ? handleMouseMove : undefined}
            >
                <div 
                    ref={infiniteScrollConfig ? infiniteScrollContainerRef : undefined}
                    className={(fixedHeight || infiniteScrollConfig) ? "overflow-x-auto overflow-y-auto flex-1" : ""}
                > */}

{/* // Determine if drag-to-scroll should be on the inner scroll container(this is implemented for handling fixed vertical scroll with horizontal scroll)  we can just remove the below div and comment out the above div (because the above one work with vertical,infinite and Horizntal the below code is for using vertical(fixed)+Horizontal(click and Drag))              */}
<div
  ref={!useInnerScrollForDrag && overflowVisible ? scrollContainerRef : undefined}
  className={`bg-white border border-[#043E6133] rounded-[5px] p-[5px] ${topClassName ?? ""} ${
    fixedHeight || infiniteScrollConfig 
      ? "flex flex-col" 
      : overflowVisible && !useInnerScrollForDrag
        ? "overflow-x-auto cursor-grab active:cursor-grabbing" 
        : paddingBottomNeeded 
          ? "" 
          : "overflow-hidden"
  }`}
  style={(fixedHeight || infiniteScrollConfig) ? { maxHeight: typeof fixedHeight === 'number' ? `${fixedHeight}px` : fixedHeight } : undefined}
  onMouseDown={!useInnerScrollForDrag && overflowVisible ? handleMouseDown : undefined}
  onMouseLeave={!useInnerScrollForDrag && overflowVisible ? handleMouseLeaveOrUp : undefined}
  onMouseUp={!useInnerScrollForDrag && overflowVisible ? handleMouseLeaveOrUp : undefined}
  onMouseMove={!useInnerScrollForDrag && overflowVisible ? handleMouseMove : undefined}
>
  <div 
    ref={
      infiniteScrollConfig
        ? infiniteScrollContainerRef
        : useInnerScrollForDrag
          ? scrollContainerRef
          : undefined
    }
    className={(fixedHeight || infiniteScrollConfig) ? "overflow-x-auto overflow-y-auto flex-1" : ""}
    onMouseDown={useInnerScrollForDrag ? handleMouseDown : undefined}
    onMouseLeave={useInnerScrollForDrag ? handleMouseLeaveOrUp : undefined}
    onMouseUp={useInnerScrollForDrag ? handleMouseLeaveOrUp : undefined}
    onMouseMove={useInnerScrollForDrag ? handleMouseMove : undefined}
  >
   
                
                <table className="w-full">
                    <thead className={`bg-[#e3e4e9] ${(fixedHeight || infiniteScrollConfig) && enableStickyHeader ? "sticky top-0 z-10" : ""}`}>
                    <tr>
                        {columns.map((col) => (
                            <th
                                key={String(col.key)}
                                style={{ width: col.width }}
                                className={`px-3 py-3 text-xs font-medium uppercase tracking-wider text-${
                                    col.align || "left"
                                }${noWrapHeaders ? " whitespace-nowrap" : ""}${col.className ? " " + col.className : ""}`}
                            >
                                {col.sortable !== false && enableSorting ? (
                                    <div
                                        className={`flex items-center cursor-pointer leading-none gap-1 ${
                                            col.align === "right" ? "justify-end" : 
                                            col.align === "center" ? "justify-center" : 
                                            "justify-start"
                                        }`}
                                        onClick={() => handleSort(String(col.key))}
                                    >
                                        {col.label}
                                        {getSortIcon(String(col.key))}
                                    </div>
                                ) : (
                                    <div>{col.label}</div>
                                )}
                            </th>
                        ))}
                        {getRowActions && (
                            <th className="px-3 py-3 text-xs font-medium uppercase tracking-wider text-center">
                                {t("table.actions")}
                            </th>
                        )}
                    </tr>
                </thead>

                {visibleData == null && (
                    <tbody>
                        <tr>
                            <td
                                colSpan={getRowActions ? columns.length + 1 : columns.length}
                                className="px-3 py-3 text-sm text-center"
                            >
                                <Loader />
                            </td>
                        </tr>
                    </tbody>
                )}
                {visibleData?.length === 0 && (
                    <tbody>
                        <tr>
                            <td
                                colSpan={getRowActions ? columns.length + 1 : columns.length}
                                className="px-3 py-3 text-sm text-center"
                            >
                                {t("table.noDataAvailable")}
                            </td>
                        </tr>
                    </tbody>
                )}

                {visibleData && visibleData.length > 0 && (
                    <tbody>
                        {(pagination ? paginatedData : visibleData ?? [])?.map((item) => {
                            const isDragging = draggedItem?.id === item.id;
                            const isDragOver = dragOverItem?.id === item.id;
                            const isExpanded = expandedRowId === item.id;
                            const canDrop = Boolean(
                                draggedItem &&
                                tableData &&
                                draggedItem.level === item.level &&
                                draggedItem.parentId === item.parentId &&
                                draggedItem.id !== item.id &&
                                // Prevent dropping on descendants
                                !getAllDescendants(draggedItem.id, tableData).some((desc) => desc.id === item.id)
                            );

                            return (
                                <TableBodyRow
                                    key={String(item.id)}
                                    item={item}
                                    columns={columns}
                                    enableExpand={enableExpand}
                                    indentSize={indentSize}
                                    isExpanded={isExpanded}
                                    isDragging={isDragging}
                                    isDragOver={isDragOver}
                                    canDrop={canDrop}
                                    dropIndicatorClass={getDropIndicatorClass(item)}
                                    draggableRow={canDragItem(item)}
                                    getRowActions={getRowActions}
                                    renderRowDetails={renderRowDetails}
                                    rowDetailsLoadingLabel={rowDetailsLoadingLabel}
                                    isDetailsLoading={
                                        showRowDetailsLoading &&
                                        isExpanded &&
                                        detailsReadyId !== item.id
                                    }
                                    toggleExpanded={toggleExpanded}
                                    toggleRowDetails={toggleRowDetails}
                                    onDragStart={handleDragStart}
                                    onDragOver={handleDragOver}
                                    onDragLeave={handleDragLeave}
                                    onDrop={handleDrop}
                                    onDragEnd={handleDragEnd}
                                />
                            );
                        })}
                    </tbody>
                )}
                </table>
                </div>
            </div>
        </div>
    );
}

export default ReusableTable;
